import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, vModelSelect as _vModelSelect, withDirectives as _withDirectives, unref as _unref, vModelText as _vModelText, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "section content-container" }
const _hoisted_2 = {
  key: 1,
  class: "container is-fluid"
}
const _hoisted_3 = { class: "columns" }
const _hoisted_4 = { class: "column" }
const _hoisted_5 = { class: "columns" }
const _hoisted_6 = { class: "column is-6" }
const _hoisted_7 = { class: "field" }
const _hoisted_8 = { class: "control select" }
const _hoisted_9 = ["value"]
const _hoisted_10 = {
  key: 0,
  class: "table is-fullwidth"
}
const _hoisted_11 = ["id", "onClick"]
const _hoisted_12 = { class: "template-status-column" }
const _hoisted_13 = { class: "template-icons-column" }
const _hoisted_14 = ["id", "onClick"]
const _hoisted_15 = { class: "label" }
const _hoisted_16 = { key: 0 }
const _hoisted_17 = { class: "columns domain-body" }
const _hoisted_18 = { class: "column is-6" }
const _hoisted_19 = {
  key: 0,
  class: "field"
}
const _hoisted_20 = { class: "control" }
const _hoisted_21 = {
  key: 0,
  class: "help is-danger"
}
const _hoisted_22 = { key: 1 }
const _hoisted_23 = { key: 2 }
const _hoisted_24 = {
  key: 0,
  class: "padding-above"
}
const _hoisted_25 = { class: "margin-above table is-fullwidth" }
const _hoisted_26 = {
  key: 1,
  class: "margin-above"
}

import { HANDLE_SERIOUS_ERROR } from '@/store/app/actionTypes';
import { emailApi } from '@/features/settings/services/emailApiService';
import Modal from '@/shared/components/Modal.vue';
import { EmailTemplateViewModel } from '@/features/settings/models/emailTemplateViewModel';
import TitledSection from '@/shared/components/TitledSection.vue';
import LoadingSpinner from '@/shared/components/loadingSpinner/LoadingSpinner.vue';
import Toast from '@/shared/services/toast';
import { maxLength } from '@vuelidate/validators';
import { EmailWorkspaceModel } from '@/features/settings/models/response/emailWorkspaceModel';
import Badge from '@/shared/components/Badge.vue';
import { useStore } from 'vuex';
import { computed, onMounted, ref } from 'vue';
import useVuelidate from '@vuelidate/core';
import { useRouter } from 'vue-router';


export default /*@__PURE__*/_defineComponent({
  __name: 'Email',
  setup(__props) {

const store = useStore();
const currentWorkspace = computed(() => store.getters.currentWorkspace);
const handleSeriousError = (error: string) => store.dispatch(HANDLE_SERIOUS_ERROR, error);

const isLoading = ref(true);
const updateInProgress = ref(false);
const verifyInProgress = ref(false);
const showDeleteTemplateModal = ref(false);
const deleteTemplateModel = ref<EmailTemplateViewModel>();
const activeTemplateId = ref('');
const lastActiveTemplateId = ref('');
const domain = ref('');
const domainVerified = ref(false);
const domainSaved = ref(false);
const templates = ref<EmailTemplateViewModel[]>([]);
const hasTemplates = computed(() => templates.value.length > 0);

// should reflect what is currently stored on the server, only change with responses from the server
const savedEmailWorkspace = ref<EmailWorkspaceModel>();

const validations = {
  activeTemplateId: {},
  domain: { maxLength: maxLength(67) },
};

const $v = useVuelidate(validations, { activeTemplateId, domain });

const router = useRouter();

onMounted(async () => {
  try {
    await loadTemplates();
  } catch (error) {
    if (error.status !== 404) {
      handleSeriousError(error.message);
    }
  } finally {
    isLoading.value = false;
  }
});

async function loadTemplates() {
  templates.value = await emailApi.getTemplates(currentWorkspace.value.id);
  await loadConfiguration();
}

async function loadConfiguration() {
  const response: EmailWorkspaceModel = await emailApi.getEmailWorkspace(currentWorkspace.value.id);
  setConfig(response);
}

function setConfig(emailWorkspace: EmailWorkspaceModel) {
  savedEmailWorkspace.value = emailWorkspace;

  domain.value = emailWorkspace.outboundEmailDomain;
  domainVerified.value = emailWorkspace.outboundEmailDomainVerified;
  domainSaved.value = !!domain.value;

  activeTemplateId.value = emailWorkspace.activeTemplateId;
  lastActiveTemplateId.value = emailWorkspace.activeTemplateId;
}

function getStatus(template: EmailTemplateViewModel): string {
  const templateActive = template.templateId === lastActiveTemplateId.value;
  if (templateActive) {
    return 'Active';
  } else {
    return 'Inactive';
  }
}

function getStatusClass(template: EmailTemplateViewModel): 'success' | 'disabled' {
  const templateActive = template.templateId === lastActiveTemplateId.value;
  if (templateActive) {
    return 'success';
  } else {
    return 'disabled';
  }
}

function openTemplateEditorForCreate() {
  router.push({ name: 'email-template-create', params: {} });
}

function editTemplate(template: EmailTemplateViewModel) {
  router.push({
    name: 'email-template-edit',
    params: { templateId: template.templateId },
    query: { templateName: template.name },
  });
}

function showDeleteTemplateConfirmation(template: EmailTemplateViewModel) {
  showDeleteTemplateModal.value = true;
  deleteTemplateModel.value = template;
}

async function deleteTemplate() {
  try {
    await emailApi.deleteEmailTemplate(currentWorkspace.value.id, deleteTemplateModel.value!.templateId);
    loadTemplates();
  } catch (err) {
    Toast.showError(`Failed to delete template ${err}`);
  } finally {
    showDeleteTemplateModal.value = false;
  }
}

function canSave() {
  const invalid = $v.value.$invalid;
  const activeTemplateTouched = activeTemplateId.value !== lastActiveTemplateId.value;
  const domainTouched = $v.value.domain.$dirty;
  return (activeTemplateTouched || domainTouched) && !invalid;
}

async function save() {
  try {
    updateInProgress.value = true;
    const response: EmailWorkspaceModel = await emailApi.saveEmailWorkspace(currentWorkspace.value.id, {
      activeTemplateId: activeTemplateId.value,
      outboundEmailDomain: domain.value,
    });
    setConfig(response);
    Toast.showSuccess(`Email configuration successfully saved`);
  } catch (err) {
    Toast.showError(`Failed to save email configuration ${err}`);
  } finally {
    updateInProgress.value = false;
  }
}

async function deleteDomain() {
  const response: EmailWorkspaceModel = await emailApi.saveEmailWorkspace(currentWorkspace.value.id, {
    activeTemplateId: savedEmailWorkspace.value!.activeTemplateId,
    outboundEmailDomain: '',
  });

  Toast.showSuccess('Successfully cleared company domain');
  setConfig(response);
}

async function verifyDomain() {
  try {
    verifyInProgress.value = true;
    const verifyResponse = await emailApi.verifyDomain(currentWorkspace.value.id);

    if (verifyResponse.success) {
      domainVerified.value = true;
      Toast.showSuccess(`Domain is now verified`);
    } else {
      Toast.showInfo(verifyResponse.errorMessage);
    }
  } catch (err) {
    Toast.showError(`Failed to verify domain. ${err.message}`);
  } finally {
    verifyInProgress.value = false;
  }
}

return (_ctx: any,_cache: any) => {
  const _component_n_loading_spinner = _resolveComponent("n-loading-spinner")!
  const _component_n_button = _resolveComponent("n-button")!
  const _component_font_awesome_icon = _resolveComponent("font-awesome-icon")!
  const _component_n_button_group = _resolveComponent("n-button-group")!

  return (_openBlock(), _createElementBlock("section", _hoisted_1, [
    (isLoading.value)
      ? (_openBlock(), _createBlock(_component_n_loading_spinner, { key: 0 }))
      : _createCommentVNode("", true),
    (!isLoading.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _createElementVNode("div", _hoisted_4, [
              _createVNode(TitledSection, { class: "container is-fluid island" }, {
                title: _withCtx(() => _cache[5] || (_cache[5] = [
                  _createTextVNode("Email templates")
                ])),
                subtitle: _withCtx(() => _cache[6] || (_cache[6] = [
                  _createTextVNode("Customise emails sent to your users with your company branding")
                ])),
                actions: _withCtx(() => [
                  _createVNode(_component_n_button, {
                    id: "create-new-email-template-button",
                    variant: "primary",
                    onClick: openTemplateEditorForCreate
                  }, {
                    default: _withCtx(() => _cache[7] || (_cache[7] = [
                      _createTextVNode(" Create template ")
                    ])),
                    _: 1
                  })
                ]),
                default: _withCtx(() => [
                  _createElementVNode("div", null, [
                    _createElementVNode("div", _hoisted_5, [
                      _createElementVNode("div", _hoisted_6, [
                        _createElementVNode("div", _hoisted_7, [
                          _cache[9] || (_cache[9] = _createElementVNode("label", {
                            class: "label",
                            for: "activeTemplate"
                          }, "Select the active template", -1)),
                          _createElementVNode("div", _hoisted_8, [
                            _withDirectives(_createElementVNode("select", {
                              id: "activeTemplate",
                              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((activeTemplateId).value = $event))
                            }, [
                              _cache[8] || (_cache[8] = _createElementVNode("option", {
                                value: "",
                                selected: ""
                              }, "-- Default Template --", -1)),
                              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(templates.value, (template) => {
                                return (_openBlock(), _createElementBlock("option", {
                                  key: template.templateId,
                                  value: template.templateId
                                }, _toDisplayString(template.name), 9, _hoisted_9))
                              }), 128))
                            ], 512), [
                              [_vModelSelect, activeTemplateId.value]
                            ])
                          ])
                        ])
                      ])
                    ]),
                    (hasTemplates.value)
                      ? (_openBlock(), _createElementBlock("table", _hoisted_10, [
                          _cache[10] || (_cache[10] = _createElementVNode("thead", null, [
                            _createElementVNode("tr", null, [
                              _createElementVNode("th", null, "Name"),
                              _createElementVNode("th", null, "Status"),
                              _createElementVNode("th")
                            ])
                          ], -1)),
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(templates.value, (template) => {
                            return (_openBlock(), _createElementBlock("tr", {
                              class: "templateRow",
                              key: template.templateId
                            }, [
                              _createElementVNode("td", null, [
                                _createElementVNode("a", {
                                  id: 'edit-template-link-' + template.templateId,
                                  onClick: ($event: any) => (editTemplate(template))
                                }, _toDisplayString(template.name), 9, _hoisted_11)
                              ]),
                              _createElementVNode("td", _hoisted_12, [
                                _createVNode(Badge, {
                                  type: getStatusClass(template)
                                }, {
                                  default: _withCtx(() => [
                                    _createTextVNode(_toDisplayString(getStatus(template)), 1)
                                  ]),
                                  _: 2
                                }, 1032, ["type"])
                              ]),
                              _createElementVNode("td", _hoisted_13, [
                                _createElementVNode("span", {
                                  id: 'delete-template-' + template.templateId,
                                  onClick: ($event: any) => (showDeleteTemplateConfirmation(template)),
                                  class: "icon is-small"
                                }, [
                                  _createVNode(_component_font_awesome_icon, { icon: "trash" })
                                ], 8, _hoisted_14)
                              ])
                            ]))
                          }), 128))
                        ]))
                      : _createCommentVNode("", true)
                  ]),
                  (showDeleteTemplateModal.value)
                    ? (_openBlock(), _createBlock(Modal, {
                        key: 0,
                        onCancel: _cache[2] || (_cache[2] = ($event: any) => (showDeleteTemplateModal.value = false))
                      }, {
                        title: _withCtx(() => _cache[11] || (_cache[11] = [
                          _createElementVNode("p", { class: "modal-card-title" }, "Delete email template", -1)
                        ])),
                        body: _withCtx(() => [
                          _createElementVNode("label", _hoisted_15, [
                            _createTextVNode(" Are you sure you want to delete template '" + _toDisplayString(deleteTemplateModel.value?.name) + "'? ", 1),
                            (deleteTemplateModel.value?.templateId === activeTemplateId.value)
                              ? (_openBlock(), _createElementBlock("p", _hoisted_16, " Upon delete, the default Connect template will become the active template. "))
                              : _createCommentVNode("", true)
                          ])
                        ]),
                        buttons: _withCtx(() => [
                          _createVNode(_component_n_button_group, null, {
                            default: _withCtx(() => [
                              _createVNode(_component_n_button, {
                                variant: "primary",
                                onClick: deleteTemplate
                              }, {
                                default: _withCtx(() => _cache[12] || (_cache[12] = [
                                  _createTextVNode("Delete ")
                                ])),
                                _: 1
                              }),
                              _createVNode(_component_n_button, {
                                onClick: _cache[1] || (_cache[1] = ($event: any) => (showDeleteTemplateModal.value = false)),
                                variant: "primary--outline"
                              }, {
                                default: _withCtx(() => _cache[13] || (_cache[13] = [
                                  _createTextVNode("Cancel ")
                                ])),
                                _: 1
                              })
                            ]),
                            _: 1
                          })
                        ]),
                        _: 1
                      }))
                    : _createCommentVNode("", true)
                ]),
                _: 1
              }),
              _createVNode(TitledSection, { class: "container is-fluid island" }, {
                title: _withCtx(() => _cache[14] || (_cache[14] = [
                  _createTextVNode("Outbound email address")
                ])),
                subtitle: _withCtx(() => _cache[15] || (_cache[15] = [
                  _createTextVNode("Send emails from your company email address")
                ])),
                default: _withCtx(() => [
                  _createElementVNode("div", _hoisted_17, [
                    _createElementVNode("div", _hoisted_18, [
                      (!domainSaved.value)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_19, [
                            _cache[16] || (_cache[16] = _createElementVNode("label", {
                              class: "label",
                              for: "domain"
                            }, "Enter your company domain", -1)),
                            _createElementVNode("div", _hoisted_20, [
                              _withDirectives(_createElementVNode("input", {
                                type: "text",
                                class: _normalizeClass(["input", { 'is-danger': _unref($v).domain.$invalid && _unref($v).domain.$dirty }]),
                                id: "domain",
                                placeholder: "E.g. yourdomain.com",
                                "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((domain).value = $event)),
                                onInput: _cache[4] || (_cache[4] = ($event: any) => (_unref($v).domain.$touch()))
                              }, null, 34), [
                                [_vModelText, domain.value]
                              ]),
                              (_unref($v).domain.$invalid && !_unref($v).domain.required && _unref($v).domain.$dirty)
                                ? (_openBlock(), _createElementBlock("p", _hoisted_21, " Domain is required "))
                                : _createCommentVNode("", true)
                            ])
                          ]))
                        : _createCommentVNode("", true),
                      (domainSaved.value)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_22, [
                            _createVNode(Badge, {
                              type: domainVerified.value ? 'success' : 'danger',
                              size: "medium"
                            }, {
                              default: _withCtx(() => [
                                _createElementVNode("b", null, _toDisplayString(domainVerified.value ? 'Verified' : 'Unverified') + " : ", 1),
                                _createTextVNode("   " + _toDisplayString(domain.value), 1)
                              ]),
                              _: 1
                            }, 8, ["type"]),
                            _createElementVNode("span", {
                              id: "delete-domain-button",
                              onClick: deleteDomain,
                              class: "icon is-small"
                            }, [
                              _createVNode(_component_font_awesome_icon, { icon: "trash" })
                            ])
                          ]))
                        : _createCommentVNode("", true),
                      (!domainVerified.value && domainSaved.value)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_23, [
                            (!domainVerified.value || !domainSaved.value)
                              ? (_openBlock(), _createElementBlock("div", _hoisted_24, [
                                  _cache[20] || (_cache[20] = _createTextVNode(" The following DNS records need adding before domain can be verified and emails sent. ")),
                                  _createElementVNode("table", _hoisted_25, [
                                    _createElementVNode("tr", null, [
                                      _cache[17] || (_cache[17] = _createElementVNode("th", { class: "domain-key-column" }, "Name", -1)),
                                      _createElementVNode("td", null, "connect._domainkey." + _toDisplayString(domain.value ? domain.value : '<your_domain>'), 1)
                                    ]),
                                    _cache[18] || (_cache[18] = _createElementVNode("tr", null, [
                                      _createElementVNode("th", null, "Type"),
                                      _createElementVNode("td", null, "CNAME")
                                    ], -1)),
                                    _cache[19] || (_cache[19] = _createElementVNode("tr", null, [
                                      _createElementVNode("th", null, "Value"),
                                      _createElementVNode("td", null, "dkim.onconnect.app")
                                    ], -1))
                                  ])
                                ]))
                              : _createCommentVNode("", true),
                            (domainSaved.value)
                              ? (_openBlock(), _createElementBlock("div", _hoisted_26, [
                                  _createVNode(_component_n_button, {
                                    id: "verify-domain-button",
                                    onClick: verifyDomain,
                                    loading: verifyInProgress.value,
                                    variant: "primary"
                                  }, {
                                    default: _withCtx(() => _cache[21] || (_cache[21] = [
                                      _createElementVNode("span", null, "Verify Domain", -1)
                                    ])),
                                    _: 1
                                  }, 8, ["loading"])
                                ]))
                              : _createCommentVNode("", true)
                          ]))
                        : _createCommentVNode("", true)
                    ])
                  ])
                ]),
                _: 1
              }),
              _createVNode(_component_n_button, {
                id: "save-template-button",
                onClick: save,
                variant: "primary",
                loading: updateInProgress.value,
                disabled: !canSave()
              }, {
                default: _withCtx(() => _cache[22] || (_cache[22] = [
                  _createTextVNode(" Save ")
                ])),
                _: 1
              }, 8, ["loading", "disabled"])
            ])
          ])
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})